import axios from 'axios';
import { nextTick } from 'vue';
import {
  PaymentMethodType,
  PaymentStatus,
  PIN_REQUIRED_THRESHOLD,
  DEFAULT_TRANSACTION_LIMIT,
  CARD_RAIL_LABEL,
  CARD_FAILURE_MESSAGE,
  CHECKOUT_ABANDONED_MESSAGE,
  GENERIC_FAILURE_MESSAGE,
  pendingMessage,
} from '@/constants/Payment/payment-methods';
import { calculateFees, calculateTotal } from '@/constants/Payment/transaction-fees';
import { BANK_ENUM } from '@/constants/MyGiving/bank-list';
import EGIRO_STATES from '@/constants/MyGiving/EGIRO_STATES';
import {
  getPaymentTransaction,
  listPaymentTransactions,
  reconcilePaymentTransaction,
} from '@/utils/payments-api';
import { openHitPayCheckout } from '@/utils/hitpay-checkout';
import { humanizeProgramSlug } from '@/utils/program-name';
import {
  isPaymentDemoActive,
  paymentDemoPollTimings,
  demoLinkedAccounts,
  demoFastStatus,
} from '@/utils/payment-demo';

const FAST_POLL_INTERVAL = 1000;
const FAST_POLL_MIN_WAIT = 3000;
const FAST_POLL_MAX_RETRIES = 30;

// HitPay ledger polling — after the hosted checkout closes, we poll the ledger
// (flipped to PAID by the HitPay webhook) to confirm before showing success.
// The webhook normally lands in seconds. If the row is still PENDING past
// LEDGER_RECONCILE_AFTER_MS the webhook is late, so we ask the server to
// reconcile against HitPay once; past LEDGER_POLL_MAX_MS we stop and show
// "pending" (the background sweep + notification settle it server-side).
const LEDGER_POLL_INTERVAL = 2000;
const LEDGER_RECONCILE_AFTER_MS = 20000;
const LEDGER_POLL_MAX_MS = 90000;

// How long a non-PAID terminal row (FAILED/EXPIRED/CANCELLED) is held as
// PROVISIONAL before we believe it.
//
// HitPay's hosted checkout lets the member re-enter a card after a decline, and
// each attempt fires its own webhook. So one checkout session can settle the
// ledger FAILED (first card declined) and then PAID (second card accepted) —
// the API deliberately allows that override, "money truth wins". Resolving on
// the first FAILED we happened to observe told the member their payment failed
// while HitPay had taken the money, and it stopped the in-session unlock.
// Reconcile cannot rescue it either: the server no-ops on a terminal row.
//
// So a failure has to sit still for this long before it counts. It costs a
// genuinely-declined payment a few extra seconds on the processing spinner,
// which is the right trade against telling someone their successful payment
// failed.
//
// 8s was too short to cover a member re-entering a card, but lengthening it to
// cover that was never the fix: it would have held EVERY decline behind a
// spinner for as long as a retry might take. The retry race only ever existed
// because web started polling while the checkout tab was still open — native
// waits on `browserFinished`/`appUrlOpen`, so by the time it polls the member
// has finished with HitPay and cannot retry. `openHitPayCheckout` now gates web
// on the same signal (page visibility), so both platforms only poll after the
// checkout is closed.
//
// What is left for this window to cover is webhook ORDERING, not member
// behaviour: two attempts inside one checkout can both be in flight as it
// closes, and the first attempt's FAILED can land after the second's PAID.
// Seconds, not minutes.
const LEDGER_FAILED_GRACE_MS = 20000;

// Legacy unscoped key — purged on load so a previous member's history can never
// leak to a new member on a shared device. History is now keyed per member.
const STORAGE_KEY = 'hogc_payment_history';

function historyKey(memberId) {
  return memberId ? `${STORAGE_KEY}_${memberId}` : null;
}

function loadHistory(memberId) {
  const key = historyKey(memberId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(memberId, history) {
  const key = historyKey(memberId);
  if (!key) return; // never persist to a shared/unscoped key
  try {
    localStorage.setItem(key, JSON.stringify(history));
  } catch {
    // Storage full or unavailable — silently fail
  }
}

// Ledger status (CREATED|PENDING|PAID|FAILED|EXPIRED|CANCELLED|REFUNDED) → the
// UI status the My Payments screen renders.
function ledgerStatusToUi(status) {
  switch (status) {
    case 'PAID':
      return PaymentStatus.SUCCESS;
    case 'PENDING':
    case 'CREATED':
      return PaymentStatus.PENDING;
    case 'REFUNDED':
      return PaymentStatus.REFUNDED;
    case 'FAILED':
    case 'EXPIRED':
    case 'CANCELLED':
    default:
      return PaymentStatus.FAILED;
  }
}

/** Map a ledger transaction row to the history entry shape the UI renders. */
// Course purchases reach the ledger with no productName and no metadata — the
// API opens them as { productType: 'course-program', productId: <slug> } and
// nothing else (course-purchase.service.ts). So paymentTitle fell through to the
// product-type label and the row read "Course fee".
//
// That only showed up on SOME rows, which is what made it look random: a row the
// sheet finished on THIS device also has a local entry carrying
// "<program> book", and the merge below prefers it. Any row without that local
// twin — an attempt where the member closed the sheet after a decline, a
// purchase made on another device, anything after the local history is cleared —
// had nothing but the generic label. Two rows for the same product, two names.
//
// The slug is enough to say what was bought, so say it, and phrase it exactly as
// the local entry does so the two cannot disagree.
const COURSE_PRODUCT_TYPES = ['course-program', 'course'];
function courseProductName(row) {
  if (!COURSE_PRODUCT_TYPES.includes(row?.productType) || !row?.productId) return null;
  return `${humanizeProgramSlug(row.productId)} book`;
}

function ledgerRowToEntry(row) {
  const meta = row.metadata || {};
  // The settled method (card brand/last4 / PayNow) is captured server-side into
  // metadata.paymentMethod by the webhook; fall back to the generic rail.
  const pm = meta.paymentMethod || null;
  return {
    transactionId: row.reference,
    amount: (row.amountCents || 0) / 100,
    totalPaid: (row.amountCents || 0) / 100,
    feesCovered: false,
    feesAmount: 0,
    paymentMethod: {
      type: pm?.type === 'bank' ? PaymentMethodType.BANK : PaymentMethodType.CARD,
      // The ledger's own captured label wins when it has one — "Visa •••• 4242"
      // tells the member more than any generic name can.
      label: pm?.label || CARD_RAIL_LABEL,
      id: 'hitpay',
    },
    productName: row.productName || courseProductName(row) || null,
    referenceCode: meta.referenceCode || row.productId || null,
    category: row.productType || null,
    description: meta.description || null,
    metadata: meta,
    status: ledgerStatusToUi(row.status),
    // Prefer the settlement time for display/ordering, else when it was opened.
    timestamp: row.paidAt || row.createdAt || new Date().toISOString(),
    updatedAt: row.paidAt || row.createdAt || new Date().toISOString(),
    _fromLedger: true,
  };
}

/**
 * Merge authoritative ledger rows over the locally-recorded history. The ledger
 * is the source of truth for status/amount of the transactions it owns (HitPay
 * card + server-created). Local-only entries the ledger doesn't own — FAST bank
 * transfers (giving API) and wallet mocks — are preserved. Keyed by
 * transactionId (== ledger reference). Newest first, capped at 100.
 */
function mergeLedgerHistory(local, ledgerRows) {
  const byId = new Map();
  (local || []).forEach(entry => {
    if (entry?.transactionId) byId.set(entry.transactionId, entry);
  });

  ledgerRows.forEach(row => {
    const mapped = ledgerRowToEntry(row);
    const existing = byId.get(mapped.transactionId);
    if (existing) {
      // Take the ledger's authoritative status/amount/timestamps. Adopt the
      // ledger's captured method (e.g. "Visa •••• 4242") + product name when it
      // has them; otherwise keep the local entry's richer generic label.
      byId.set(mapped.transactionId, {
        ...existing,
        status: mapped.status,
        amount: mapped.amount,
        // Ledger is authoritative for amount on matched rows — don't keep the
        // stale local total.
        totalPaid: mapped.totalPaid,
        description: existing.description || mapped.description,
        category: existing.category || mapped.category,
        // `row.productName`, not `mapped.productName`: mapped now carries a name
        // DERIVED from the slug for course rows, which must not outrank the local
        // entry's own — that one came from the loaded program list and is the
        // real name where the humanized slug is only a readable stand-in.
        // Order: what the ledger actually stored, else the local entry, else the
        // derived fallback.
        productName: row.productName || existing.productName || mapped.productName,
        paymentMethod: row.metadata?.paymentMethod ? mapped.paymentMethod : existing.paymentMethod,
        updatedAt: mapped.updatedAt,
        _fromLedger: true,
      });
    } else {
      byId.set(mapped.transactionId, mapped);
    }
  });

  return Array.from(byId.values())
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 100);
}

const INITIAL_STATE = {
  // Sheet visibility
  isOpen: false,
  currentStep: 'method', // 'method' | 'pin' | 'processing' | 'success'

  // Payment request (set by caller)
  request: null,

  // User selections
  selectedMethod: null,
  coverFees: false,

  // Processing state
  isProcessing: false,
  result: null,

  // Payment history (persisted to localStorage, keyed by member — loaded lazily
  // via loadMemberHistory once the signed-in member is known).
  paymentHistory: [],
  historyMemberId: null,

  // Demo-only: when the mock eGIRO link flow (scenario 6) "links" an account,
  // this holds the resulting accounts so the linkedBankAccounts getter — which
  // reads it reactively — flips the sheet from the empty state to payable.
  demoLinkedOverride: null,

  // FAST polling
  _pollTimer: null,

  // Promise resolver (internal — used to resolve the openPaymentSheet promise)
  _resolver: null,
};

export default {
  namespaced: true,

  state: () => ({ ...INITIAL_STATE }),

  getters: {
    transactionLimit(state) {
      return state.request?.transactionLimit || DEFAULT_TRANSACTION_LIMIT;
    },

    // Same rule giving-api enforces on POST /e-giro/fast: the amount actually sent
    // (the total, incl. covered fees) at or above the threshold needs the PIN.
    requiresPin(state, getters) {
      return getters.totalAmount >= PIN_REQUIRED_THRESHOLD;
    },

    feesAmount(state) {
      if (!state.request?.amount) return 0;
      return calculateFees(state.request.amount);
    },

    totalAmount(state) {
      if (!state.request?.amount) return 0;
      return calculateTotal(state.request.amount, state.coverFees);
    },

    selectedMethodLabel(state) {
      if (!state.selectedMethod) return '';
      if (state.selectedMethod.type === PaymentMethodType.BANK) {
        return `${state.selectedMethod.bankName} Account ${state.selectedMethod.lastFour}`;
      }
      return state.selectedMethod.label;
    },

    /**
     * Get linked bank accounts from myGiving store (single source of truth).
     * Filters to only LINKED-state accounts and maps to payment method shape.
     */
    linkedBankAccounts(state, getters, rootState) {
      // Read the override first so this getter re-runs when the mock link flow
      // sets it (scenario 6). Falls back to the scripted demo accounts.
      if (isPaymentDemoActive()) return state.demoLinkedOverride || demoLinkedAccounts();
      const myGivingAccounts = rootState.myGiving?.linkedAccounts || [];
      return (
        myGivingAccounts
          // Must accept the SAME states as myGiving/linkedBankAccounts. Matching
          // only 'Linked' here is what left a pre-migration 'Success' mandate
          // visible in Manage but still unusable at the payment sheet — the half
          // of ONE-8186 the Manage-side fix did not actually close.
          .filter(acc => acc.state === EGIRO_STATES.LINKED || acc.state === EGIRO_STATES.SUCCESS)
          .map(acc => {
            // Resolve display fields from the canonical bank list keyed by bankCode
            // (same source the My Giving screens use), not ad-hoc account fields.
            const bank = BANK_ENUM[acc.bankCode];
            const bankName = bank?.name || acc.bankName || acc.bankCode;
            const lastFour = (acc.maskedBankAccNumber || '').replace(/\D/g, '').slice(-4);
            return {
              id: acc.eGiroMandateId,
              type: PaymentMethodType.BANK,
              bankCode: acc.bankCode,
              bankName,
              lastFour,
              iconPath: bank?.iconPath || '',
              label: `${bankName} Account ${lastFour}`.trim(),
              eGiroMandateId: acc.eGiroMandateId,
              // Preserve original account data for FAST payload
              _raw: acc,
            };
          })
      );
    },

    hasPaymentMethods(state, getters) {
      return getters.linkedBankAccounts.length > 0;
    },
  },

  mutations: {
    SET_OPEN(state, isOpen) {
      state.isOpen = isOpen;
    },

    SET_STEP(state, step) {
      state.currentStep = step;
    },

    SET_REQUEST(state, request) {
      state.request = request;
    },

    SET_SELECTED_METHOD(state, method) {
      state.selectedMethod = method;
    },

    SET_COVER_FEES(state, coverFees) {
      state.coverFees = coverFees;
    },

    SET_PROCESSING(state, isProcessing) {
      state.isProcessing = isProcessing;
    },

    SET_RESULT(state, result) {
      state.result = result;
    },

    SET_RESOLVER(state, resolver) {
      state._resolver = resolver;
    },

    SET_POLL_TIMER(state, timer) {
      state._pollTimer = timer;
    },

    ADD_TO_HISTORY(state, entry) {
      state.paymentHistory.unshift(entry);
      // Keep last 100 entries
      if (state.paymentHistory.length > 100) {
        state.paymentHistory = state.paymentHistory.slice(0, 100);
      }
      saveHistory(state.historyMemberId, state.paymentHistory);
    },

    UPDATE_HISTORY_STATUS(state, { transactionId, status }) {
      const entry = state.paymentHistory.find(e => e.transactionId === transactionId);
      if (entry) {
        entry.status = status;
        entry.updatedAt = new Date().toISOString();
        saveHistory(state.historyMemberId, state.paymentHistory);
      }
    },

    SET_HISTORY_MEMBER(state, memberId) {
      state.historyMemberId = memberId;
    },

    SET_HISTORY(state, history) {
      state.paymentHistory = history;
    },

    SET_DEMO_LINKED_OVERRIDE(state, accounts) {
      state.demoLinkedOverride = accounts;
    },

    RESET(state) {
      if (state._pollTimer) {
        clearTimeout(state._pollTimer);
      }
      const { paymentHistory, historyMemberId } = state;
      Object.assign(state, { ...INITIAL_STATE, paymentHistory, historyMemberId });
    },
  },

  actions: {
    /**
     * Load the signed-in member's locally-persisted history into state, keyed by
     * member id. Purges the legacy unscoped key so a previous member's history
     * can never leak to a new member on a shared device. Idempotent per member;
     * swaps the in-memory list when the member changes.
     */
    loadMemberHistory({ commit, state, rootState }) {
      // Purge the legacy unscoped key (pre member-scoping) if present.
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
      const memberId = rootState.profile?.id ?? null;
      if (state.historyMemberId === memberId) return;
      commit('SET_HISTORY_MEMBER', memberId);
      commit('SET_HISTORY', loadHistory(memberId));
    },

    /**
     * Fetch linked accounts from myGiving store.
     * Called when the payment sheet opens to ensure fresh data.
     */
    async fetchLinkedAccounts({ dispatch }) {
      try {
        await dispatch('myGiving/getLinkedBankAccounts', null, { root: true });
      } catch {
        // Silently fail — user can still use Apple Pay / Google Pay
      }
    },

    async openPaymentSheet({ commit, state, dispatch, getters }, request) {
      // Resolve any prior pending promise before starting a new flow
      if (state._resolver) {
        state._resolver({ status: PaymentStatus.CANCELLED });
        commit('SET_RESOLVER', null);
      }

      // Force a genuine closed→open transition when the sheet is already flagged
      // open. Without this, a state that says "open" while nothing is on screen
      // makes every later attempt a no-op: the sheet presents on the v-model
      // going false→true (see docs/ION_MODAL_PATTERN.md), so a value that never
      // changes never presents, and the caller waits on a promise only the sheet
      // can resolve. That is the "stuck on loading, can't reopen checkout" bug —
      // the second tap did all its work and displayed nothing.
      //
      // The await is not decoration: RESET and SET_OPEN in one tick collapse into
      // a single reactive update, so the watcher never sees false.
      if (state.isOpen) {
        commit('RESET');
        await nextTick();
      }

      // Ensure history is loaded under the current member (so a new payment is
      // recorded to the right member-scoped key), then fetch linked accounts.
      dispatch('loadMemberHistory');
      dispatch('fetchLinkedAccounts');

      return new Promise(resolve => {
        commit('SET_REQUEST', {
          amount: request.amount,
          description: request.description,
          referenceCode: request.referenceCode || null,
          // Set by callers that already opened the transaction server-side
          // (e.g. courses/purchaseProgram). payWithHitPay presents THIS checkout
          // rather than opening a second one — dropping them here silently
          // breaks the whole card flow.
          displayReference: request.displayReference || null,
          checkoutUrl: request.checkoutUrl || null,
          category: request.category || null,
          transactionLimit: request.transactionLimit || DEFAULT_TRANSACTION_LIMIT,
          allowFeesCoverage: request.allowFeesCoverage !== false,
          // Whether linked bank accounts (eGIRO/FAST) may be used for this
          // request. Giving: yes. Purchases: NOT YET — a bank-paid purchase
          // settles on the giving service, which has no link back to the
          // payment ledger, so the money leaves and the product never unlocks.
          // Re-enable per product once that rail settles against the ledger
          // (ClickUp epic 86eynr4tx, subtask 86eynr4y7). Defaults to true so
          // every existing giving call site is unaffected.
          allowBankRail: request.allowBankRail !== false,
          preselectedMethod: request.preselectedMethod || null,
          metadata: request.metadata || {},
        });

        commit('SET_RESOLVER', resolve);
        commit('SET_COVER_FEES', false);
        commit('SET_RESULT', null);
        // Fresh checkout → fresh idempotency key (minted lazily on first attempt).
        // Clear any prior demo link so each run starts from its scripted accounts.
        commit('SET_DEMO_LINKED_OVERRIDE', null);
        commit('SET_STEP', 'method');

        // Preselect a linked account if the caller named one — the method view
        // shows it already chosen so the user can slide straight away.
        if (request.preselectedMethod) {
          const preselected =
            getters.linkedBankAccounts.find(a => a.id === request.preselectedMethod) || null;
          commit('SET_SELECTED_METHOD', preselected);
        } else {
          commit('SET_SELECTED_METHOD', null);
        }

        commit('SET_OPEN', true);
      });
    },

    // Inline selection inside the method view — just records the choice; the
    // user confirms by sliding (or the card tile opens hosted checkout).
    selectMethod({ commit }, method) {
      commit('SET_SELECTED_METHOD', method);
    },

    goBackToMethod({ commit }) {
      commit('SET_STEP', 'method');
    },

    /**
     * Pay via HitPay's hosted checkout.
     *
     * The caller has ALREADY opened the transaction server-side (e.g.
     * `courses/purchaseProgram`) — these are server-only product types, so price
     * and eligibility are decided there and the client never sends an amount. It
     * passes the resulting `referenceCode` + `checkoutUrl` in on the request.
     *
     *   1. Open that hosted checkout URL in the system in-app browser.
     *   2. Poll the ledger. The browser outcome is NOT a payment signal: a user
     *      can pay and then close the browser, and HitPay's redirect is forgeable.
     *      BOTH outcomes poll — the ledger is the only source of truth.
     */
    async payWithHitPay({ commit, state, dispatch, rootState }) {
      const request = { ...state.request };
      const token = rootState.user.token;

      commit('SET_SELECTED_METHOD', {
        id: 'hitpay',
        type: PaymentMethodType.CARD,
        label: CARD_RAIL_LABEL,
      });
      commit('SET_PROCESSING', true);
      commit('SET_STEP', 'processing');

      const baseEntry = {
        transactionId: null,
        amount: request.amount,
        totalPaid: request.amount,
        feesCovered: false,
        feesAmount: 0,
        paymentMethod: {
          type: PaymentMethodType.CARD,
          label: CARD_RAIL_LABEL,
          id: 'hitpay',
        },
        productName: request.description || null,
        referenceCode: request.referenceCode,
        category: request.category,
        description: request.description,
        metadata: request.metadata,
        timestamp: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      try {
        const reference = request.referenceCode;
        const { displayReference, checkoutUrl } = request;

        if (!reference || !checkoutUrl) {
          throw new Error(CARD_FAILURE_MESSAGE);
        }

        baseEntry.transactionId = reference;
        // What the member quotes to finance — the same string HitPay puts on the
        // statement. Falls back to the opaque id for product types with no series.
        baseEntry.referenceCode = displayReference || reference;

        await openHitPayCheckout({ url: checkoutUrl });

        // The sheet was dismissed while the browser was up.
        if (!state.isOpen) {
          commit('SET_PROCESSING', false);
          return;
        }

        const status = await dispatch('pollLedgerStatus', { reference, token });

        if (!state.isOpen) {
          commit('SET_PROCESSING', false);
          return;
        }

        if (status === 'PAID') {
          const result = { ...baseEntry, status: PaymentStatus.SUCCESS };
          commit('SET_RESULT', result);
          commit('ADD_TO_HISTORY', result);
        } else if (status === 'FAILED') {
          const result = {
            ...baseEntry,
            status: PaymentStatus.FAILED,
            error: 'payment_failed',
            message: CARD_FAILURE_MESSAGE,
          };
          commit('SET_RESULT', result);
          commit('ADD_TO_HISTORY', result);
        } else if (status === 'ABANDONED') {
          // The checkout ended without a completed attempt. Same failed state on
          // the receipt, different words: nothing was declined, so the decline
          // copy would describe an attempt the member never made.
          const result = {
            ...baseEntry,
            status: PaymentStatus.FAILED,
            error: 'checkout_abandoned',
            message: CHECKOUT_ABANDONED_MESSAGE,
          };
          commit('SET_RESULT', result);
          commit('ADD_TO_HISTORY', result);
        } else {
          // Still PENDING — the webhook confirmation is late. The server sweep
          // reconciles it; never present this as a failure, the money may be gone.
          const result = {
            ...baseEntry,
            status: PaymentStatus.PENDING,
            error: 'payment_pending',
            message: pendingMessage('card'),
          };
          commit('SET_RESULT', result);
          commit('ADD_TO_HISTORY', result);
        }

        commit('SET_PROCESSING', false);
        commit('SET_STEP', 'success');
      } catch (error) {
        if (!state.isOpen) {
          commit('SET_PROCESSING', false);
          return;
        }
        commit('SET_RESULT', {
          ...baseEntry,
          status: PaymentStatus.FAILED,
          error: 'payment_error',
          message: error?.response?.data?.message || error.message || CARD_FAILURE_MESSAGE,
        });
        commit('SET_PROCESSING', false);
        commit('SET_STEP', 'success');
      }
    },

    toggleCoverFees({ commit, state }) {
      commit('SET_COVER_FEES', !state.coverFees);
    },

    /**
     * Process payment — routes to FAST for bank accounts, mock for digital wallets.
     */
    async processPayment({ commit, state, getters, dispatch, rootState }) {
      // Snapshot values before async gap
      const request = { ...state.request };
      const method = { ...state.selectedMethod };
      const totalPaid = getters.totalAmount;
      const feesAmount = state.coverFees ? getters.feesAmount : 0;
      const feesCovered = state.coverFees;
      const methodLabel = getters.selectedMethodLabel;

      commit('SET_PROCESSING', true);
      commit('SET_STEP', 'processing');

      try {
        if (method.type === PaymentMethodType.BANK) {
          // === FAST bank transfer ===
          const accessToken = rootState.user.token;
          let transactionId;
          let status;

          if (isPaymentDemoActive()) {
            // Demo: skip the giving API; show processing briefly, then a scripted outcome.
            await new Promise(r => setTimeout(r, 900));
            transactionId = `demo_fast_${Date.now().toString(36)}`;
            status = demoFastStatus();
          } else {
            const payLoad = {
              egiroRef: method.eGiroMandateId,
              amount: totalPaid,
              currency: 'SGD',
              givingCategory: request.category || 'Offering',
              paymentDetails: request.description,
            };

            const response = await axios.post(
              `${process.env.VUE_APP_GIVING_API_V2_URL}/e-giro/fast`,
              payLoad,
              { headers: { Authorization: `Bearer ${accessToken}` } },
            );

            transactionId = response.data?.transactionId || response.data?.id;

            if (!transactionId) {
              throw new Error('No transaction ID returned from FAST');
            }

            // Poll for transaction status
            status = await dispatch('pollFastStatus', {
              transactionId,
              accessToken,
            });
          }

          // Guard: if sheet was dismissed during polling, bail out
          if (!state.isOpen) {
            commit('SET_PROCESSING', false);
            return;
          }

          const baseEntry = {
            transactionId,
            amount: request.amount,
            totalPaid,
            feesCovered,
            feesAmount,
            paymentMethod: { type: method.type, label: methodLabel, id: method.id },
            referenceCode: request.referenceCode,
            category: request.category,
            description: request.description,
            timestamp: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          if (status === 'SUCCESS') {
            const result = { ...baseEntry, status: PaymentStatus.SUCCESS };
            commit('SET_RESULT', result);
            commit('ADD_TO_HISTORY', result);
            commit('SET_PROCESSING', false);
            commit('SET_STEP', 'success');
          } else {
            // PENDING or FAILED
            const pendingStatus =
              status === 'PENDING' ? PaymentStatus.PENDING : PaymentStatus.FAILED;
            const result = {
              ...baseEntry,
              status: pendingStatus,
              error: status === 'PENDING' ? 'payment_pending' : 'payment_failed',
              message:
                status === 'PENDING'
                  ? pendingMessage('bank')
                  : // No "you have not been charged" here: a FAST transfer settles
                    // through the giving service, and that it cannot debit on
                    // failure has not been established. See CARD_FAILURE_MESSAGE.
                    GENERIC_FAILURE_MESSAGE,
            };
            commit('SET_RESULT', result);
            commit('ADD_TO_HISTORY', result);
            commit('SET_PROCESSING', false);
            commit('SET_STEP', 'success');
          }
        } else {
          // === Apple Pay / Google Pay (mock for now) ===
          await new Promise(r => setTimeout(r, 1500));

          if (!state.isOpen || !state.request) {
            commit('SET_PROCESSING', false);
            return;
          }

          const transactionId = `txn_${Date.now().toString(36)}_${Math.random()
            .toString(36)
            .slice(2, 8)}`;
          const result = {
            status: PaymentStatus.SUCCESS,
            transactionId,
            amount: request.amount,
            totalPaid,
            feesCovered,
            feesAmount,
            paymentMethod: { type: method.type, label: methodLabel, id: method.id },
            referenceCode: request.referenceCode,
            category: request.category,
            description: request.description,
            metadata: request.metadata,
            timestamp: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          commit('SET_RESULT', result);
          commit('ADD_TO_HISTORY', result);
          commit('SET_PROCESSING', false);
          commit('SET_STEP', 'success');
        }
      } catch (error) {
        if (!state.isOpen) {
          commit('SET_PROCESSING', false);
          return;
        }

        const result = {
          status: PaymentStatus.FAILED,
          error: 'payment_error',
          message: error?.response?.data?.message || error.message || GENERIC_FAILURE_MESSAGE,
        };
        commit('SET_RESULT', result);
        commit('SET_PROCESSING', false);
        commit('SET_STEP', 'success');
      }
    },

    /**
     * Poll FAST transaction status until SUCCESS, FAILED, or max retries.
     */
    pollFastStatus({ commit, state }, { transactionId, accessToken }) {
      return new Promise(resolve => {
        const startTime = Date.now();
        let retries = 0;

        const poll = async () => {
          if (!state.isOpen) {
            resolve('CANCELLED');
            return;
          }

          try {
            const response = await axios.get(
              `${process.env.VUE_APP_GIVING_API_V2_URL}/e-giro/fast/status/${transactionId}`,
              { headers: { Authorization: `Bearer ${accessToken}` } },
            );

            const txStatus = response.data?.status;
            const elapsed = Date.now() - startTime;

            if (txStatus === 'SUCCESS') {
              resolve('SUCCESS');
              return;
            }

            if (txStatus === 'FAILED') {
              resolve('FAILED');
              return;
            }

            // Still PENDING
            retries++;
            if (retries >= FAST_POLL_MAX_RETRIES || elapsed >= FAST_POLL_MIN_WAIT * 10) {
              resolve('PENDING');
              return;
            }

            const timer = setTimeout(poll, FAST_POLL_INTERVAL);
            commit('SET_POLL_TIMER', timer);
          } catch {
            retries++;
            if (retries >= FAST_POLL_MAX_RETRIES) {
              resolve('PENDING');
              return;
            }
            const timer = setTimeout(poll, FAST_POLL_INTERVAL);
            commit('SET_POLL_TIMER', timer);
          }
        };

        // Wait minimum time before first poll
        const timer = setTimeout(poll, FAST_POLL_MIN_WAIT);
        commit('SET_POLL_TIMER', timer);
      });
    },

    /**
     * Poll the payment ledger by reference until it settles or times out.
     * Reads our own ledger cheaply every LEDGER_POLL_INTERVAL. If the webhook is
     * late (still PENDING past LEDGER_RECONCILE_AFTER_MS) it asks the server to
     * reconcile against HitPay once; past LEDGER_POLL_MAX_MS it gives up to a
     * 'pending' result (the background sweep settles it server-side).
     *
     * PAID settles immediately. A non-PAID terminal does NOT: it is held for
     * LEDGER_FAILED_GRACE_MS first, because two attempts inside one hosted
     * checkout can both be in flight as it closes, and the first attempt's
     * FAILED can land after the second's PAID.
     *
     * Resolves to 'PAID' | 'FAILED' | 'ABANDONED' | 'PENDING' | 'CANCELLED',
     * where FAILED is a decline, ABANDONED is a ledger EXPIRED/CANCELLED row
     * (a checkout that ended with no attempt), and CANCELLED means the member
     * dismissed the sheet while we were still polling.
     */
    pollLedgerStatus({ commit, state }, { reference, token }) {
      return new Promise(resolve => {
        const startTime = Date.now();
        let reconcileRequested = false;

        // When we first saw a non-PAID terminal status. null = not yet; once set it
        // stays set — the ledger cannot reopen a terminal row, so PAID is the only
        // thing that can still overturn it, and a later non-terminal reading (only
        // the demo harness produces one) must not reset the clock.
        let failedSince = null;
        // And WHICH terminal it was, so the timeout below reports the outcome we
        // actually saw rather than flattening an expiry into a decline.
        let provisionalOutcome = null;

        // Demo mode uses fast timings so scenarios resolve in a couple seconds.
        const timings = isPaymentDemoActive()
          ? paymentDemoPollTimings()
          : {
              intervalMs: LEDGER_POLL_INTERVAL,
              reconcileAfterMs: LEDGER_RECONCILE_AFTER_MS,
              maxMs: LEDGER_POLL_MAX_MS,
            };
        // Demo timings predate the grace window; scale it to the demo's own
        // interval so a scripted failure still resolves in a couple of seconds.
        const failedGraceMs = isPaymentDemoActive()
          ? timings.intervalMs * 2
          : LEDGER_FAILED_GRACE_MS;

        // Map a ledger status to a poll outcome. 'PAID' is final immediately;
        // 'FAILED' and 'ABANDONED' are non-PAID terminals held as provisional;
        // null = keep waiting.
        //
        // ABANDONED (ledger EXPIRED/CANCELLED) is kept apart from FAILED because
        // the two need different words: a decline is something that happened to
        // an attempt the member made, an expiry is a checkout that ended without
        // one. Collapsing them told members "the payment didn't go through" about
        // a payment they had never tried to make.
        const outcomeFor = status => {
          if (status === 'PAID') return 'PAID';
          if (status === 'FAILED') return 'FAILED';
          if (status === 'CANCELLED' || status === 'EXPIRED') return 'ABANDONED';
          return null;
        };

        // A non-PAID terminal reading only ends the poll once it has held for
        // the grace window. Returns true when the caller should keep polling.
        const failureStillProvisional = (now, outcome) => {
          if (failedSince === null) {
            failedSince = now;
            provisionalOutcome = outcome;
          }
          return now - failedSince < failedGraceMs;
        };

        // Ends the poll if this outcome is final, and reports whether it did. The
        // ledger read and the reconcile below both settle on the same terms, so
        // they share one implementation rather than two that can drift.
        const settle = outcome => {
          if (outcome === 'PAID') {
            resolve('PAID');
            return true;
          }
          const isNonPaidTerminal = outcome === 'FAILED' || outcome === 'ABANDONED';
          if (isNonPaidTerminal && !failureStillProvisional(Date.now(), outcome)) {
            resolve(outcome);
            return true;
          }
          return false;
        };

        // How every non-settling pass ends: give up if we are out of time,
        // otherwise come back after the interval. The ledger read and its error
        // path both end this way, and they were two hand-kept copies — the
        // ABANDONED work had to make the same edit inside each of them.
        const giveUpOrRetry = () => {
          if (Date.now() - startTime >= timings.maxMs) {
            // A failure we have actually seen is a better answer than "pending",
            // even if its grace window never got to run out.
            resolve(failedSince === null ? 'PENDING' : provisionalOutcome);
            return;
          }
          const timer = setTimeout(poll, timings.intervalMs);
          commit('SET_POLL_TIMER', timer);
        };

        const poll = async () => {
          if (!state.isOpen) {
            resolve('CANCELLED');
            return;
          }

          try {
            const tx = await getPaymentTransaction({ token, reference });
            if (settle(outcomeFor(tx?.status))) return;

            const elapsed = Date.now() - startTime;

            // Webhook is late — ask the server to reconcile against HitPay once.
            // Only while the row is still open: the server no-ops on a terminal
            // row, so asking during a provisional failure spends the one
            // reconcile we get and answers nothing.
            if (
              !reconcileRequested &&
              failedSince === null &&
              elapsed >= timings.reconcileAfterMs
            ) {
              reconcileRequested = true;
              try {
                const settled = await reconcilePaymentTransaction({ token, reference });
                if (settle(outcomeFor(settled?.status))) return;
              } catch {
                // Reconcile failed — keep polling; the background sweep is the backstop.
              }
            }

            giveUpOrRetry();
          } catch {
            giveUpOrRetry();
          }
        };

        poll();
      });
    },

    /**
     * Verify PIN via the existing myGiving PIN system.
     * Returns the verification result.
     */
    async verifyPin({ dispatch }, { pin }) {
      return dispatch('myGiving/verifyPin', { pin }, { root: true });
    },

    /**
     * Load the member's real payment history from the ledger and merge it over
     * local history, so My Payments reflects ALL their payments (incl. ones made
     * on another device or created server-side) with authoritative status — not
     * just what this device happened to record. Called on the My Payments page.
     *
     * Degrades gracefully: if the ledger list endpoint isn't reachable (older
     * backend, offline), the locally-recorded history is left untouched.
     */
    async fetchLedgerHistory({ commit, state, dispatch, rootState }) {
      if (isPaymentDemoActive()) return; // never hit the real API in demo mode
      const token = rootState.user?.token;
      if (!token) return;

      // Make sure the in-memory list is the current member's before merging.
      dispatch('loadMemberHistory');

      try {
        const rows = await listPaymentTransactions({ token });
        const merged = mergeLedgerHistory(state.paymentHistory, rows);
        commit('SET_HISTORY', merged);
        saveHistory(state.historyMemberId, merged);
      } catch {
        // Endpoint missing / network error — keep local history as-is.
      }
    },

    /**
     * Refresh statuses of all pending FAST transactions in history.
     * Called from the My Payments page on pull-to-refresh.
     */
    async refreshPendingStatuses({ commit, state, rootState }) {
      const accessToken = rootState.user.token;
      const pending = state.paymentHistory.filter(
        e =>
          e.status === PaymentStatus.PENDING &&
          (e.paymentMethod?.type === PaymentMethodType.BANK ||
            e.paymentMethod?.type === PaymentMethodType.CARD),
      );

      await Promise.allSettled(
        pending.map(async entry => {
          try {
            // Card pendings resolve against the payment ledger (webhook /
            // reconcile-driven), not the giving FAST endpoint.
            if (entry.paymentMethod?.type === PaymentMethodType.CARD) {
              const tx = await getPaymentTransaction({
                token: accessToken,
                reference: entry.transactionId,
              });
              const ledgerStatus = tx?.status;
              if (ledgerStatus === 'PAID') {
                commit('UPDATE_HISTORY_STATUS', {
                  transactionId: entry.transactionId,
                  status: PaymentStatus.SUCCESS,
                });
              } else if (
                ledgerStatus === 'FAILED' ||
                ledgerStatus === 'EXPIRED' ||
                ledgerStatus === 'CANCELLED'
              ) {
                commit('UPDATE_HISTORY_STATUS', {
                  transactionId: entry.transactionId,
                  status: PaymentStatus.FAILED,
                });
              }
              return;
            }

            const response = await axios.get(
              `${process.env.VUE_APP_GIVING_API_V2_URL}/e-giro/fast/status/${entry.transactionId}`,
              { headers: { Authorization: `Bearer ${accessToken}` } },
            );
            const txStatus = response.data?.status;
            if (txStatus === 'SUCCESS') {
              commit('UPDATE_HISTORY_STATUS', {
                transactionId: entry.transactionId,
                status: PaymentStatus.SUCCESS,
              });
            } else if (txStatus === 'FAILED') {
              commit('UPDATE_HISTORY_STATUS', {
                transactionId: entry.transactionId,
                status: PaymentStatus.FAILED,
              });
            }
          } catch {
            // Keep as pending if we can't reach the API
          }
        }),
      );
    },

    closeSheet({ commit, state }) {
      const resolver = state._resolver;
      const result = state.result || { status: PaymentStatus.CANCELLED };

      commit('RESET');

      if (resolver) {
        resolver(result);
      }
    },
  },
};
